import * as React from "react";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { Add } from "@mui/icons-material";
import { Box, Divider, Typography } from "@mui/material";
import {
  StyledMenuItemContainer,
  HtmlTooltipDisabled,
} from "../StyledComponents/styled";
import {
  estimateOptionsContentWidth,
  sortSelectorOptions,
} from "./selectorOptionUtils";
import SelectFieldShell from "./SelectFieldShell";
import SelectorOptionsMessage from "./SelectorOptionsMessage";
import useSelectDropdown from "./useSelectDropdown";
import {
  SELECTED_OPTION_COLOR,
  SELECT_COLOR,
  SELECT_HIGHLIGHT_COLOR,
} from "./selectConstants";

export default function SingleSelect({
  label,
  defaultVal,
  elemId,
  options,
  onChangeCall,
  fieldError,
  enableFullWidth,
  selectionCheck,
  sortOptions,
  readOnlyFlag,
  fieldInfo,
  allowClearance,
  usedFor,
  disableSearch,
  disabledOptions,
  tooltipTitleForDisabledOptions,
  flip,
  leadingOption,
}) {
  const optionRefs = useRef({});
  const [selectedValue, setSelectedValue] = useState(defaultVal);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [instanceId] = useState(
    () => elemId || `single-select-${Math.random().toString(36).substr(2, 9)}`
  );

  const isDisabled = typeof readOnlyFlag !== "undefined" ? readOnlyFlag : false;
  const isFilter = usedFor === "filter";
  // Filter dropdowns always allow clearing; non-filter dropdowns fall back to the allowClearance prop
  const canClear = isFilter ? true : (typeof allowClearance !== "undefined" ? allowClearance : false);
  const applySorting = typeof sortOptions !== "undefined" ? sortOptions : true;
  const showSearch = typeof disableSearch !== "undefined" ? !disableSearch : true;
  const handleDropdownClosed = useCallback(() => {
    setHighlightedIndex(-1);
  }, []);

  const dropdown = useSelectDropdown({
    instanceId,
    showSearch,
    forceTop: flip,
    onClosed: handleDropdownClosed,
  });
  const {
    open,
    setOpen,
    searchTerm,
    isFocused,
    close: closeDropdown,
  } = dropdown;

  // Keep the wrapper's historical close signature so selection and keyboard
  // handlers remain easy to compare with the pre-refactor implementation.
  const close = useCallback(
    (shouldRefocus = false) =>
      closeDropdown({ clearSearch: true, refocus: shouldRefocus }),
    [closeDropdown]
  );

  // Empty { label: "" } (owner change clearing Brand Company) must not float the label.
  const hasSelectedLabel =
    selectedValue != null &&
    selectedValue !== "" &&
    typeof selectedValue === "object" &&
    String(selectedValue.label ?? "").trim() !== "";
  const shrink = isFocused || open || hasSelectedLabel || isFilter;

  useEffect(() => {
    setSelectedValue(defaultVal);
  }, [defaultVal]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const handleSelect = useCallback((option) => {
    if (isDisabled) return;

    setSelectedValue(option);
    onChangeCall(option);
    setHighlightedIndex(-1);
    close();
  }, [isDisabled, onChangeCall, close]);

  const handleClear = (e) => {
    e.stopPropagation();
    if (isDisabled) return;

    setSelectedValue(null);
    onChangeCall(null);
  };

  // null means the filter pane is still fetching this list.
  const sourceOptions = options === null ? [] : options;
  const sortedOptions = useMemo(() => sortSelectorOptions(sourceOptions, applySorting), [sourceOptions, applySorting]);

  const filteredOptions = useMemo(() => {
    if (!showSearch) return sortedOptions;

    const searchValue = searchTerm.trim().toLowerCase();
    if (!searchValue) return sortedOptions;

    return sortedOptions.filter((option) => {
      const optionLabel = String(option?.label ?? "").toLowerCase();
      return optionLabel.includes(searchValue);
    });
  }, [sortedOptions, searchTerm, showSearch]);

  // Pinned row (e.g. Add to new company) stays above the divider and is not searched
  const hasLeadingOption =
    typeof leadingOption !== "undefined" &&
    leadingOption !== null &&
    typeof leadingOption.label !== "undefined";
  const listOptions = hasLeadingOption === true ? [leadingOption, ...filteredOptions] : filteredOptions;

  const isOptionDisabledFn = useCallback(
    (option) => typeof disabledOptions === "function" && disabledOptions(option),
    [disabledOptions]
  );

  const scrollHighlightedIntoView = useCallback((index) => {
    const element = optionRefs.current[`option-${index}`];
    if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  const handleListKeyDown = useCallback(
    (e) => {
      if (!open) return;

      const totalCount = listOptions.length;
      if (totalCount === 0 && e.key !== "Escape") return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev < totalCount - 1 ? prev + 1 : 0;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalCount - 1;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          if (highlightedIndex >= 0 && highlightedIndex < listOptions.length) {
            const option = listOptions[highlightedIndex];
            if (!isOptionDisabledFn(option)) handleSelect(option);
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;

        default:
          break;
      }
    },
    [open, listOptions, highlightedIndex, close, isOptionDisabledFn, scrollHighlightedIntoView, handleSelect]
  );

  // Reported to the shell, which decides the final panel width.
  const dropdownContentWidth = useMemo(
    () =>
      estimateOptionsContentWidth(
        [
          ...(hasLeadingOption === true ? [leadingOption] : []),
          ...sortedOptions,
        ].map((option) => option?.label)
      ),
    [sortedOptions, hasLeadingOption, leadingOption]
  );

  return (
    <SelectFieldShell
      dropdown={dropdown}
      elemId={elemId}
      label={label}
      shrink={shrink}
      disabled={isDisabled}
      enableFullWidth={enableFullWidth}
      canClear={canClear && Boolean(selectedValue)}
      onClear={handleClear}
      fieldError={fieldError}
      endAdornment={fieldInfo}
      showSearch={showSearch}
      onAnchorClick={() => {
        if (!isDisabled) setOpen((previous) => !previous);
      }}
      onAnchorKeyDown={(event) => {
        if (isDisabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
        if (event.key === "Escape") close();
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (!open) setOpen(true);
        }
      }}
      onListKeyDown={handleListKeyDown}
      disablePortal={false}
      contentWidth={dropdownContentWidth}
      valueDisplay={
        <Typography
          variant="body2"
          sx={{
            color: isDisabled ? "#62626E" : SELECT_COLOR,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {selectedValue ? selectedValue.label : isFilter ? "All Results" : ""}
        </Typography>
      }
    >
      {listOptions.map((option, index) => {
        const optionKey = option?.label ?? index;
        const isLeadingRow = hasLeadingOption === true && index === 0;
        const isSelectedOption =
          selectedValue && selectionCheck(option, selectedValue);
        const isOptionDisabled = isOptionDisabledFn(option);
        const isHighlighted = highlightedIndex === index;

        const menuItem = (
          <StyledMenuItemContainer
            key={isOptionDisabled ? undefined : optionKey}
            ref={(element) => {
              optionRefs.current[`option-${index}`] = element;
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (!isOptionDisabled) handleSelect(option);
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onMouseEnter={() => setHighlightedIndex(index)}
            sx={{
              display: "flex",
              alignItems: "center",
              marginRight: 0,
              marginTop:0.5,
              backgroundColor: isHighlighted
                ? SELECT_HIGHLIGHT_COLOR
                : isSelectedOption
                ? SELECTED_OPTION_COLOR
                : "transparent",
              opacity: isOptionDisabled ? 0.5 : 1,
              cursor: isOptionDisabled ? "default" : "pointer",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {isLeadingRow === true && (
                <Add sx={{ fontSize: 18, color: SELECT_COLOR }} />
              )}
              <Typography
                variant="body2"
                className="selectorOptions"
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  color: isLeadingRow || isSelectedOption ? SELECT_COLOR : "inherit",
                  fontWeight: isLeadingRow === true ? 700 : isSelectedOption ? 500 : 400,
                }}
              >
                {option.label}
              </Typography>
            </Box>
          </StyledMenuItemContainer>
        );

        const row = isOptionDisabled ? (
          <HtmlTooltipDisabled
            key={optionKey}
            title={tooltipTitleForDisabledOptions ?? "This option is unavailable"}
            arrow
          >
            <span>{menuItem}</span>
          </HtmlTooltipDisabled>
        ) : (
          menuItem
        );

        if (isLeadingRow !== true) return row;

        return (
          <Box key={optionKey}>
            {row}
            {/* <Divider sx={{ my: 0 }} /> */}
          </Box>
        );
      })}

      <SelectorOptionsMessage options={options} hasVisibleOptions={filteredOptions.length > 0} />
    </SelectFieldShell>
  );
}
